// What a practice round may draw. A mode is a filter over a key's (item kind,
// skill), not over exercises: every review builder in review-table.ts is
// either always audio or never audio, so the skill alone decides whether a
// question is answered by ear. lesson.test.ts fails if a builder breaks that.
//
// React-free, and never imports lesson.ts: lesson.ts imports this.
import type { Course } from "./content/course.ts";
import type { ItemKind, ProgressKey, SkillId, UnitDef } from "./content/types.ts";
import { itemIdOf, skillOf } from "./content/types.ts";
import { isDue, started, trainedKeys } from "./progress/query.ts";
import { canReview, sentencesOf, type SentencePrompt } from "./review-table.ts";
import type { GameState } from "./state.ts";
import { feedsPractice } from "./tracks.ts";

export const PRACTICE_MODES = ["mix", "mistakes", "letters", "words", "listen", "write"] as const;
export type PracticeMode = (typeof PRACTICE_MODES)[number];

export const MISTAKE_WINDOW_DAYS = 14;
const DAY = 86_400_000;

type Allows = (kind: ItemKind, skill: SkillId) => boolean;
const only =
	(...pairs: `${ItemKind}:${SkillId}`[]): Allows =>
	(kind, skill) =>
		pairs.includes(`${kind}:${skill}`);

/**
 * `match`: the round adds a four-letter match board by sound. `sentence`: how
 * the mode builds a sentence for a grammar form's `use` key; unset, either.
 */
export const PRACTICE: Record<
	PracticeMode,
	{ allows: Allows; match: boolean; sentence?: SentencePrompt }
> = {
	mix: { allows: () => true, match: true },
	mistakes: { allows: () => true, match: false },
	letters: { allows: (kind) => kind === "letter", match: true },
	words: { allows: (kind) => kind === "word", match: false },
	listen: {
		allows: only("letter:lower", "letter:hear", "word:read", "word:hear", "form:use"),
		match: true,
		sentence: "sound",
	},
	write: { allows: only("word:spell", "form:use"), match: false, sentence: "gloss" },
};

/** Answered wrong within the window, and no later lesson asked it without a miss. */
export function isMistake(st: GameState, k: ProgressKey, now: number = Date.now()): boolean {
	const at = st.slips[k];
	return at !== undefined && now - at < MISTAKE_WINDOW_DAYS * DAY;
}

/**
 * The units practice reaches into: started ones whose track feeds a review
 * pool, and grammar, whose `use` keys review as sentence builds.
 */
export const practiceUnits = (st: GameState, course: Course): UnitDef[] =>
	course.units.filter((u) => started(st, u) && feedsPractice(u.track));

/**
 * The keys a round in this mode may draw, in `trainedKeys` order. Trained, not
 * merely listed: a started unit also lists the :hear keys of a listen lesson
 * the learner has not played yet.
 */
export function practiceKeys(
	mode: PracticeMode,
	st: GameState,
	course: Course,
	now: number = Date.now(),
): ProgressKey[] {
	const inOpen = new Set(practiceUnits(st, course).flatMap((u) => course.unitKeys(u)));
	const { allows, sentence } = PRACTICE[mode];
	const sentences = sentencesOf(practiceUnits(st, course), course);
	return trainedKeys(st).filter((k) => {
		if (!inOpen.has(k)) return false;
		const kind = course.itemById(itemIdOf(k)).kind;
		const skill = skillOf(k);
		return (
			canReview(kind, skill, itemIdOf(k), sentences, course, sentence) &&
			allows(kind, skill) &&
			(mode !== "mistakes" || isMistake(st, k, now))
		);
	});
}

export interface ModeSummary {
	/** Keys the mode may draw: zero means a round would be empty. */
	keys: number;
	/** How many of them are due. */
	due: number;
}

/** What the Practice tab shows on each card. */
export function practiceSummary(
	st: GameState,
	course: Course,
	now: number = Date.now(),
): Record<PracticeMode, ModeSummary> {
	const of = (mode: PracticeMode): ModeSummary => {
		const keys = practiceKeys(mode, st, course, now);
		return { keys: keys.length, due: keys.filter((k) => isDue(st, k, now)).length };
	};
	return {
		mix: of("mix"),
		mistakes: of("mistakes"),
		letters: of("letters"),
		words: of("words"),
		listen: of("listen"),
		write: of("write"),
	};
}
